import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, ModalBody, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Utils } from '../../services';
import { visibleNavigation, SECTION_LABEL_KEYS } from '../../config/navigation';
import {
    INTEGRATION_NAV_KEYS,
    INTEGRATION_SUPERADMIN_KEYS,
    INTEGRATION_SEARCH_KEYWORDS,
} from '../IntegrationsForm/integrationNavKeys';
import { integrations_config_path } from '../IntegrationsForm/integrationRouteUtils';
import { track_event } from '../../services/posthogAnalytics';

import './CommandPalette.scss';

// Avoid a literal `javascript:` substring (eslint rule `no-script-url`).
const unsafe_javascript_scheme_colon = ['java', 'script', ':'].join('');
const leading_javascript_in_attr_pattern = new RegExp(`^\\s*${unsafe_javascript_scheme_colon}`, 'i');

// URL query param used to deep-link to a prefilled command palette search.
// `find` is intentionally neutral so we can broaden the palette beyond
// navigation + knowledge base in the future (movies, customers, etc.).
// Chosen to never collide with page-specific list filters like `?s=` or `?q=`.
const COMMAND_PALETTE_URL_PARAM = 'find';

// `?find=…&findMode=kb` — KB-first layout (no navigation list) until the user
// edits the input. Opening via Ctrl+K on a Kronos page uses the current route
// to prefill `find` and applies the same mode; the pair is written to the
// address bar (replaceState) and cleared when the dialog closes.
const COMMAND_PALETTE_MODE_PARAM = 'findMode';

// `?kb=<wp-id|slug>` — open the palette on a specific knowledge-base article.
// Prefer numeric WP post ID (stable across languages). Slugs still work.
// Resolved via ticketack-kb wpml-resolve-ht-kb to the UI language when possible,
// otherwise falls back to the source article (wrong language).
const COMMAND_PALETTE_KB_PARAM = 'kb';

const KB_WP_REST_BASE = 'https://ticketack.com/wp-json/wp/v2/ht-kb';
const KB_WPML_RESOLVE_URL = 'https://ticketack.com/wp-json/ticketack-kb/v1/wpml-resolve-ht-kb';

// Rebuild the query string with palette params in a stable order:
// `kb`, then `findMode`, then `find` (last). Other page params keep their order after.
function apply_command_palette_search_params(url, { kb, find_mode, find }) {
    const others = [];
    url.searchParams.forEach((value, key) => {
        if (
            key === COMMAND_PALETTE_KB_PARAM
            || key === COMMAND_PALETTE_MODE_PARAM
            || key === COMMAND_PALETTE_URL_PARAM
        ) {
            return;
        }
        others.push([key, value]);
    });
    const next = new URLSearchParams();
    if (kb != null && String(kb) !== '')
        next.set(COMMAND_PALETTE_KB_PARAM, String(kb));
    if (find_mode)
        next.set(COMMAND_PALETTE_MODE_PARAM, String(find_mode));
    if (find)
        next.set(COMMAND_PALETTE_URL_PARAM, String(find));
    others.forEach(([key, value]) => next.append(key, value));
    const qs = next.toString();
    url.search = qs ? `?${qs}` : '';
}

// Normalize a string for case/accent-insensitive matching.
//
// - Lowercase.
// - German umlauts/eszett expanded to their ASCII digraphs BEFORE stripping
//   diacritics so "ausrüstung" and "ausruestung" both normalize to the same
//   "ausruestung" (if we just stripped the diaeresis we'd get "ausrustung"
//   which would not match a user typing the ASCII form).
// - NFD + strip remaining diacritics (French and everything else).
function normalize_search(s) {
    return String(s || '')
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
}

// Common FR/EN/DE stopwords we drop from user queries so sentence-style input
// (e.g. a French question about creating contingents) still matches item keywords. Values are already
// diacritic-stripped because we tokenize the normalized query.
const SEARCH_STOPWORDS = new Set([
    // FR
    'a', 'au', 'aux', 'avec', 'ce', 'ces', 'cet', 'cette', 'comme', 'comment',
    'dans', 'de', 'des', 'du', 'elle', 'en', 'est', 'et', 'eu', 'faire', 'fait',
    'ils', 'il', 'je', 'la', 'le', 'les', 'leur', 'lui', 'ma', 'mais', 'me',
    'mes', 'mon', 'nos', 'notre', 'nous', 'on', 'ou', 'par', 'pas', 'peut',
    'peux', 'plus', 'pour', 'pourquoi', 'puis', 'quand', 'que', 'quel', 'quelle',
    'quelles', 'quels', 'qui', 'sa', 'sans', 'se', 'ses', 'son', 'sur', 'ta',
    'te', 'tes', 'ton', 'tu', 'un', 'une', 'vos', 'votre', 'vous', 'y',
    'voudrais', 'veux', 'voir', 'ajouter', 'creer', 'modifier', 'supprimer',
    'liste', 'gerer', 'gestion',
    // EN
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'can', 'do', 'for', 'from',
    'how', 'i', 'in', 'is', 'it', 'my', 'of', 'on', 'or', 'our', 'to', 'the',
    'want', 'would', 'like', 'see', 'manage', 'list', 'show', 'create', 'edit',
    'delete', 'with', 'what', 'where', 'who', 'why', 'when',
    // DE
    'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einer',
    'eines', 'einem', 'und', 'oder', 'aber', 'wie', 'was', 'wer', 'wo', 'wann',
    'warum', 'ich', 'du', 'er', 'sie', 'es', 'wir', 'ihr', 'mit', 'fuer', 'zum',
    'zur', 'zu', 'nach', 'auf', 'ueber', 'moechte', 'sehen', 'erstellen',
    'bearbeiten', 'loeschen', 'verwalten', 'liste', 'anzeigen',
]);

function tokenize_query(q) {
    // Normalized query → array of tokens we actually want to match on.
    // Splits on any non-alphanumeric character (already diacritic-stripped),
    // drops very short tokens and stopwords. Falls back to the raw tokens if
    // filtering would remove everything (so "a" by itself still searches).
    const raw = String(q || '')
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);
    if (raw.length === 0)
        return [];
    const filtered = raw.filter(tok => tok.length >= 2 && !SEARCH_STOPWORDS.has(tok));
    return filtered.length > 0 ? filtered : raw;
}

// Split normalized text into word tokens so we can distinguish "whole word"
// matches from plain substring matches when scoring.
function word_tokens(text) {
    if (!text)
        return [];
    return String(text).split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

// Score an item against the (normalized, non-stopword) query tokens.
// Returns 0 when any token is not found anywhere — such items are excluded,
// preserving the previous "all tokens must match" behaviour. Otherwise the
// score aggregates per-token hits weighted by field importance so that
// title/keyword matches outrank description-only matches and are ranked
// higher when the same token appears multiple times.
function score_item(item, tokens) {
    const label = normalize_search(item.label);
    const label_words = word_tokens(label);
    const description = normalize_search(item.description || '');
    const path = normalize_search(item.path || '');
    const keywords = (item.keywords || []).map(normalize_search);
    // Breadcrumb labels: useful when the user types the section or group name
    // (e.g. French UI strings like the movies-stats or QR-hardware entries) — weighted between keywords and
    // description so a clean hit still wins against description-only matches.
    const breadcrumb = normalize_search(
        [item.section_label, item.group_label].filter(Boolean).join(' ')
    );
    const breadcrumb_words = word_tokens(breadcrumb);

    const count_occurrences = (haystack, needle) => {
        if (!haystack || !needle)
            return 0;
        let n = 0;
        let from = 0;
        while (from <= haystack.length) {
            const idx = haystack.indexOf(needle, from);
            if (idx === -1)
                break;
            n += 1;
            from = idx + needle.length;
        }
        return n;
    };

    let total = 0;
    for (const tok of tokens) {
        let token_score = 0;

        // Word-level prefix match: matches token at word boundaries even if the
        // word itself is longer (tarif → tarifaires, film → filme, salle →
        // salles, qr → qrcode). Guarded by a minimum token length so a lone "a"
        // doesn't suddenly match everything.
        const has_word_prefix = words => tok.length >= 3 && words.some(w => w.startsWith(tok));

        // Title / label — strongest signal.
        if (label === tok)
            token_score += 200;
        else if (label_words.includes(tok))
            token_score += 80;
        else if (label.startsWith(tok))
            token_score += 60;
        else if (has_word_prefix(label_words))
            token_score += 50;
        else if (label.includes(tok))
            token_score += 30;

        // Keywords — explicit synonyms, strong signal.
        let best_keyword = 0;
        for (const kw of keywords) {
            if (!kw)
                continue;
            const kw_words = word_tokens(kw);
            if (kw === tok)
                best_keyword = Math.max(best_keyword, 40);
            else if (kw_words.includes(tok))
                best_keyword = Math.max(best_keyword, 25);
            else if (has_word_prefix(kw_words))
                best_keyword = Math.max(best_keyword, 20);
            else if (kw.includes(tok))
                best_keyword = Math.max(best_keyword, 10);
        }
        token_score += best_keyword;

        // Breadcrumb (section › group) — mid signal: lets "statistiques films"
        // find "Analyse et statistiques › … › Films" without relying on the
        // per-item description mentioning every hierarchy term.
        if (breadcrumb_words.includes(tok))
            token_score += 15;
        else if (breadcrumb.includes(tok))
            token_score += 6;

        // Description — weak signal, bonus for repeated occurrences (capped).
        const desc_hits = count_occurrences(description, tok);
        if (desc_hits > 0)
            token_score += 5 + Math.min(desc_hits - 1, 3) * 2;

        // Path — very weak tie-breaker.
        if (path.includes(tok))
            token_score += 2;

        if (token_score === 0)
            return 0;
        total += token_score;
    }

    return total;
}

function strip_html(html) {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
}

function decode_html_entities(text) {
    if (text == null)
        return '';
    // Browser-only: DOMParser safely decodes HTML entities.
    try {
        const doc = new DOMParser().parseFromString(String(text), 'text/html');
        return doc.documentElement.textContent || '';
    } catch (_err) {
        return String(text);
    }
}

/**
 * WordPress may emit sizes="auto, (max-width: N) 100vw, N" on lazy images. In narrow
 * embedded UIs the leading "auto," slot makes the browser pick a tiny srcset candidate.
 * Strip that prefix so srcset resolves like a normal responsive image.
 */
function normalize_wp_img_sizes_attribute(sizes_val) {
    const s = String(sizes_val || '').trim();
    if (!s)
        return null;
    if (/^\s*auto\s*,/i.test(s)) {
        const rest = s.replace(/^\s*auto\s*,\s*/i, '').trim();
        return rest || '(max-width: 1920px) 100vw, 1920px';
    }
    if (/^\s*auto\s*$/i.test(s))
        return '(max-width: 1920px) 100vw, 1920px';
    return s;
}

function sanitize_html(html) {
    // Minimal in-house sanitizer: remove scripts/styles/iframes and inline event handlers.
    // We keep basic formatting tags for readability in the preview.
    try {
        const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
        const remove_selectors = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta'];
        remove_selectors.forEach(sel => doc.querySelectorAll(sel).forEach(n => n.remove()));

        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
        let node = walker.currentNode;
        while (node) {
            const current = node;
            const attrs = Array.from(node.attributes || []);
            for (const a of attrs) {
                const name = (a.name || '').toLowerCase();
                const value = String(a.value || '');
                if (name.startsWith('on')) {
                    current.removeAttribute(a.name);
                    continue;
                }
                if ((name === 'href' || name === 'src') && leading_javascript_in_attr_pattern.test(value)) {
                    current.removeAttribute(a.name);
                }
            }
            node = walker.nextNode();
        }

        doc.querySelectorAll('img').forEach(img => {
            const width_attr = img.getAttribute('width');
            const height_attr = img.getAttribute('height');
            const declared_width = width_attr != null ? parseInt(String(width_attr), 10) : NaN;
            const declared_height = height_attr != null ? parseInt(String(height_attr), 10) : NaN;
            img.removeAttribute('width');
            img.removeAttribute('height');
            const inline_style = img.getAttribute('style');
            if (inline_style) {
                const cleaned = inline_style
                    .replace(/\b(?:width|height|max-width|min-width)\s*:[^;]+;?/gi, '')
                    .trim()
                    .replace(/;\s*;/g, ';');
                if (cleaned)
                    img.setAttribute('style', cleaned);
                else
                    img.removeAttribute('style');
            }

            // WordPress width/height hint display size once attributes are stripped above.
            const size_bits = [];
            if (!Number.isNaN(declared_width) && declared_width > 0)
                size_bits.push(`max-width:min(100%, ${declared_width}px)`);
            if (!Number.isNaN(declared_height) && declared_height > 0)
                size_bits.push(`max-height:${declared_height}px`);
            if (size_bits.length > 0) {
                const prev = (img.getAttribute('style') || '').trim();
                const extra = size_bits.join(';');
                img.setAttribute('style', prev ? `${prev};${extra}` : extra);
            }

            const sizes = img.getAttribute('sizes');
            if (sizes == null)
                return;
            const normalized = normalize_wp_img_sizes_attribute(sizes);
            if (normalized != null && normalized !== sizes)
                img.setAttribute('sizes', normalized);
        });

        return doc.body?.innerHTML || '';
    } catch (_err) {
        return '';
    }
}

/** Label for KB image lightbox: figure caption, WP caption text, then img alt. */
function kb_lightbox_label_for_img(el_img) {
    if (!el_img || typeof el_img.closest !== 'function')
        return '';
    const norm = s => String(s || '').replace(/\s+/g, ' ').trim();
    const fig = el_img.closest('figure');
    if (fig) {
        const fc = fig.querySelector('figcaption');
        const t = norm(fc?.textContent);
        if (t)
            return t;
    }
    const wp_wrap = el_img.closest('.wp-caption');
    if (wp_wrap) {
        const cap_el = wp_wrap.querySelector('.wp-caption-text');
        const t = norm(cap_el?.textContent);
        if (t)
            return t;
    }
    return norm(el_img.getAttribute('alt'));
}

function excerpt_from_article(article) {
    const excerpt = decode_html_entities(strip_html(article?.excerpt?.rendered));
    if (excerpt)
        return excerpt;
    const content = decode_html_entities(strip_html(article?.content?.rendered));
    if (!content)
        return null;
    return content.length > 140 ? `${content.slice(0, 140)}…` : content;
}

function normalize_ticketack_hostname(hostname) {
    return String(hostname || '').toLowerCase().replace(/^www\./, '');
}

/** Public WordPress KB article (ticketack.com/.../knowledge-base/...). */
function is_kb_article_public_url(url) {
    const h = normalize_ticketack_hostname(url.hostname);
    return h === 'ticketack.com' && String(url.pathname || '').includes('/knowledge-base/');
}

/**
 * Resolve href from WordPress-rendered HTML for palette routing.
 * Root-relative WP paths (/knowledge-base, /wp-content, …) use ticketack.com.
 */
function resolve_kb_content_href(raw) {
    if (raw == null)
        return null;
    const s = String(raw).trim();
    if (!s || s === '#' || s.toLowerCase().startsWith(unsafe_javascript_scheme_colon)
        || s.toLowerCase().startsWith('mailto:') || s.toLowerCase().startsWith('tel:'))
        return null;
    if (s.startsWith('#'))
        return null;
    if (s.toLowerCase().startsWith('data:'))
        return null;
    try {
        if (/^https?:\/\//i.test(s))
            return new URL(s);
        if (s.startsWith('//'))
            return new URL(`https:${s}`);
        if (s.startsWith('/knowledge-base') || s.startsWith('/wp-content') || s.startsWith('/wp-includes'))
            return new URL(s, 'https://ticketack.com');
        if (typeof window !== 'undefined' && window.location?.origin)
            return new URL(s, window.location.origin);
        return new URL(s, 'https://ticketack.com');
    } catch (_err) {
        return null;
    }
}

/** Wrapped screenshots, CDN assets, etc. — must not hijack the SPA URL. */
function is_direct_media_url(url) {
    const path = (url.pathname || '').toLowerCase();
    return /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico|mp4|webm)(\?.*)?$/i.test(path);
}

/** Same SPA origin or a Kronos deployment host (e.g. kronos.ticketack.com). */
function is_kronos_in_app_url(resolved, location) {
    if (!resolved || !location)
        return false;
    if (resolved.origin === location.origin)
        return true;
    const h = (resolved.hostname || '').toLowerCase();
    return h.startsWith('kronos') && h.endsWith('ticketack.com');
}

function build_palette_items({ t, i18n, user, setting, workflows }) {
    if (!user)
        return [];

    const tkt_instance = user.tkt_instance;

    // Interpolation context for descriptions: exposes the domain-specific word for
    // "screening(s)" so copy reads naturally per vertical (wording comes from i18n per locale).
    // FR/EN are lowercased to fit inline prose; German nouns stay capitalized.
    const lng = (i18n?.language || 'fr').toLowerCase().split(/[-_]/)[0];
    const lower_for_prose = s => (lng === 'de' ? String(s) : String(s || '').toLowerCase());
    const desc_ctx = {
        screening: lower_for_prose(t('common.screening', { count: 1 })),
        screenings: lower_for_prose(t('common.screening', { count: 42 })),
    };

    const nav_ctx = { user, tkt_instance, setting, workflows, pathname: '' };
    const tree = visibleNavigation(nav_ctx);
    const allowed_sections = new Set(
        Utils.appSections()
            .filter(s => user.verifyAccessSections(s.id))
            .map(s => s.id)
    );

    const items = [];
    for (const section of tree) {
        if (!allowed_sections.has(section.sectionId))
            continue;
        const section_label = t(SECTION_LABEL_KEYS[section.sectionId] || `app.section_${section.sectionId}`);
        for (const group of section.groups) {
            const group_label = t(group.labelKey);
            for (const item of group.items) {
                const label = item.label !== undefined
                    ? item.label
                    : t(item.labelKey, item.labelOptions);
                const description = item.descKey
                    ? t(item.descKey, desc_ctx)
                    : null;
                items.push({
                    id: item.id,
                    path: item.path,
                    label,
                    description,
                    keywords: item.keywords || [],
                    section_id: section.sectionId,
                    section_label,
                    group_id: group.id,
                    group_label
                });
            }
        }
    }

    // Palette-only deeplinks (do not appear in the sidebar navigation):
    // "Tous les ..." routes open the events list directly on the "All" tab.
    // Kept here (and not in navigation.js) to avoid polluting the menu.
    const all_events_deeplinks = [
        { type: 'movie', labelKey: 'events.subevents.all_movie', keywords: ['tous', 'all', 'films', 'movies', 'cinema', 'cinéma'] },
        { type: 'music_group', labelKey: 'events.subevents.all_music_group', keywords: ['tous', 'all', 'artistes', 'artists', 'groupes', 'bands'] },
        { type: 'drama', labelKey: 'events.subevents.all_drama', keywords: ['tous', 'all', 'pièces', 'pieces', 'theatre', 'théâtre', 'plays'] },
        { type: 'exhibition', labelKey: 'events.subevents.all_exhibition', keywords: ['tous', 'all', 'expositions', 'exhibitions', 'works', 'œuvres', 'oeuvres'] },
        { type: 'service', labelKey: 'events.subevents.all_service', keywords: ['tous', 'all', 'activites', 'activités', 'services', 'workshops', 'ateliers'] },
    ];
    for (const dl of all_events_deeplinks) {
        const base_path = `/events/${dl.type}`;
        const base_item = items.find(it => it.path === base_path);
        if (!base_item)
            continue;
        const label = t(dl.labelKey);
        items.push({
            ...base_item,
            id: `${base_item.id}.all`,
            path: `${base_path}/all`,
            label,
            keywords: Array.from(new Set([...(base_item.keywords || []), ...(dl.keywords || [])])),
        });
    }

    const integrations_base = items.find(it => it.path === '/settings/edit/default/integrations');
    if (integrations_base) {
        const is_superadmin = typeof user.isSuperAdmin === 'function' && user.isSuperAdmin();
        for (const key of INTEGRATION_NAV_KEYS) {
            if (INTEGRATION_SUPERADMIN_KEYS.has(key) && !is_superadmin)
                continue;

            const name = t(`settings.integrations.${key}.title`);
            const search_keywords = INTEGRATION_SEARCH_KEYWORDS[key] || [key];
            items.push({
                id: `${integrations_base.id}.${key}`,
                path: integrations_config_path('default', key),
                label: t('settings.integrations.page_title', { name }),
                description: t(`settings.integrations.${key}.description`),
                keywords: Array.from(new Set([
                    key,
                    name,
                    ...search_keywords,
                ])),
                section_id: integrations_base.section_id,
                section_label: integrations_base.section_label,
                group_id: integrations_base.group_id,
                group_label: integrations_base.group_label,
            });
        }
    }

    return items;
}

// Best matching navigation item for the current route (longest path prefix wins).
function find_context_item_by_pathname(pathname, items) {
    const p = String(pathname || '').split(/[?#]/)[0] || '';
    if (!p || p === '/')
        return null;
    let best = null;
    let best_len = -1;
    for (const item of items) {
        const ip = item.path || '';
        if (!ip)
            continue;
        if (p === ip || p.startsWith(`${ip}/`)) {
            if (ip.length > best_len) {
                best = item;
                best_len = ip.length;
            }
        }
    }
    return best;
}

function context_seed_from_pathname(pathname, items, t) {
    const item = find_context_item_by_pathname(pathname, items);
    const item_label = item?.label != null ? String(item.label).trim() : '';
    // Align with KB fetch minimum length (>= 3 normalized chars is required for a request).
    if (item_label.length >= 3)
        return item_label;

    // Section home routes (/admin, /operation, /analysis) have no nav leaf at that
    // exact path; use the same section title as the top bar so Ctrl+K is not empty.
    let p = String(pathname || '').split(/[?#]/)[0] || '';
    if (p.length > 1 && p.endsWith('/'))
        p = p.slice(0, -1);
    const section_roots = {
        [`/${Utils.SECTION_ADMIN}`]: Utils.SECTION_ADMIN,
        [`/${Utils.SECTION_OPERATION}`]: Utils.SECTION_OPERATION,
        [`/${Utils.SECTION_ANALYSIS}`]: Utils.SECTION_ANALYSIS,
    };
    const section_id = section_roots[p];
    if (!section_id)
        return '';
    const section_label = String(
        t(SECTION_LABEL_KEYS[section_id] || `app.section_${section_id}`) || ''
    ).trim();
    return section_label.length >= 3 ? section_label : '';
}

export default function CommandPalette() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const input_ref = useRef(null);
    const kb_cache_ref = useRef(new Map());
    const kb_auto_opened_for_query_ref = useRef(null);
    const open_kb_article_fn_ref = useRef(() => {});
    const open_kb_deep_link_fn_ref = useRef(() => {});
    // Value from `?kb=` (id or slug) waiting after the palette mounts / clears selection.
    const pending_kb_ref = useRef(null);
    // Stable share id written to `?kb=` (entry post id, not necessarily the displayed trad).
    const kb_share_id_ref = useRef(null);

    const { user } = useSelector(state => state.auth);
    const { setting } = useSelector(state => state.settings);
    const { workflows } = useSelector(state => state.workflows);
    const { isMobile, showNav } = useSelector(state => state.ui);

    const [open, set_open] = useState(false);
    const [query, set_query] = useState('');
    const [selected, set_selected] = useState(0);
    // When true, the navigation section is hidden until the user edits the input
    // (contextual KB-first open, or deep link with findMode=kb).
    const [kb_context_lock, set_kb_context_lock] = useState(false);
    const [context_user_edited, set_context_user_edited] = useState(false);
    const [kb_list_selected, set_kb_list_selected] = useState(0);

    const items = useMemo(
        () => build_palette_items({ t, i18n, user, setting, workflows }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, i18n, i18n.language, user, setting, workflows]
    );
    const filtered = useMemo(() => {
        const q_normalized = normalize_search(query).trim();
        if (!q_normalized)
            return items;
        const tokens = tokenize_query(q_normalized);
        if (tokens.length === 0)
            return items;
        // An item is kept when every (non-stopword) token of the query scores
        // at least one hit across label/keywords/description/path. We then sort
        // by total score so that matches in the title or keywords outrank
        // matches buried in a long description, and items where the query
        // appears multiple times (especially in keywords or title) rank higher.
        // Ties fall back to the original declaration order.
        const scored = [];
        items.forEach((item, idx) => {
            const score = score_item(item, tokens);
            if (score > 0)
                scored.push({ item, idx, score });
        });
        scored.sort((a, b) => b.score - a.score || a.idx - b.idx);
        return scored.map(x => x.item);
    }, [items, query]);

    const kb_url = useMemo(() => {
        const q = normalize_search(query).trim();
        const lng = Utils.i18nUiLang(i18n);
        if (!q || q.length < 3)
            return null;
        // Public WP REST endpoint. Note: may be blocked by CORS depending on hosting of Kronos.
        const params = new URLSearchParams();
        params.set('per_page', '5');
        params.append('status[]', 'publish');
        params.set('search', query);
        params.set('lang', lng);
        return `https://ticketack.com/wp-json/wp/v2/ht-kb?${params.toString()}`;
    }, [query, i18n]);

    const [kb_results, set_kb_results] = useState([]);
    const [kb_error, set_kb_error] = useState(false);
    const [kb_selected, set_kb_selected] = useState(null);
    const [kb_detail_loading, set_kb_detail_loading] = useState(false);
    const [kb_detail_error, set_kb_detail_error] = useState(false);
    const [kb_lightbox_src, set_kb_lightbox_src] = useState(null);
    const [kb_lightbox_alt, set_kb_lightbox_alt] = useState('');

    const close_kb_lightbox = useCallback(() => {
        set_kb_lightbox_src(null);
        set_kb_lightbox_alt('');
    }, []);

    const set_palette_offset_css_var = useCallback(() => {
        // Center relative to remaining width (minus sidebar if present).
        // Measure actual sidebar width to avoid guessing (45/220) and to react to resize.
        try {
            const narrow_viewport = window.innerWidth <= 767;
            if (narrow_viewport) {
                document.documentElement.style.setProperty('--command-palette-offset-x', '0px');
                return;
            }

            let sidebar_width = 0;
            if (showNav && !isMobile) {
                const nav_el = document.querySelector('.Nav .expandedNav, .Nav .reducedNav');
                const w = nav_el?.getBoundingClientRect?.().width;
                if (typeof w === 'number' && Number.isFinite(w) && w > 0) {
                    sidebar_width = w;
                }
            }

            // Shift by half of sidebar width (centering inside remaining viewport).
            // Clamp so we never push the dialog out of the viewport on narrow screens.
            const raw = sidebar_width / 2;
            const clamp = window.innerWidth ? Math.max(0, (window.innerWidth - 24) / 4) : 0;
            const offset = Math.max(-clamp, Math.min(raw, clamp));
            document.documentElement.style.setProperty('--command-palette-offset-x', `${Math.round(offset)}px`);
        } catch (_err) {
            // ignore
        }
    }, [isMobile, showNav]);

    useEffect(() => {
        set_palette_offset_css_var();
    }, [set_palette_offset_css_var]);

    useEffect(() => {
        const on_resize = () => set_palette_offset_css_var();
        window.addEventListener('resize', on_resize);
        return () => window.removeEventListener('resize', on_resize);
    }, [set_palette_offset_css_var]);

    useEffect(() => {
        if (!open)
            return;
        // Ensure correct offset after open animation/layout.
        window.setTimeout(() => set_palette_offset_css_var(), 0);
    }, [open, set_palette_offset_css_var]);

    const has_nav_results = filtered.length > 0;
    const has_kb_results = kb_results.length > 0;
    const hide_nav_section = kb_context_lock && !context_user_edited;
    const nav_shown_in_ui = has_nav_results && !hide_nav_section;
    const show_contact_panel = !nav_shown_in_ui && !has_kb_results;

    const normalize_kb_href = href => {
        try {
            return new URL(href, 'https://ticketack.com').toString();
        } catch (_err) {
            return null;
        }
    };

    const kb_slug_from_url = href => {
        try {
            const u = new URL(href, 'https://ticketack.com');
            const parts = String(u.pathname || '').split('/').filter(Boolean);
            const slug = parts[parts.length - 1];
            return slug || null;
        } catch (_err) {
            return null;
        }
    };

    const kb_wp_id_from_item = item => {
        if (item?.wp_id != null && Number.isFinite(Number(item.wp_id)))
            return Number(item.wp_id);
        const m = String(item?.id || '').match(/^kb\.(\d+)$/);
        return m ? Number(m[1]) : null;
    };

    const map_kb_article = a => {
        if (!a?.link)
            return null;
        return {
            id: `kb.${a.id}`,
            wp_id: a.id,
            title: a?.title?.rendered
                ? decode_html_entities(String(a.title.rendered).replace(/<[^>]*>/g, '').trim())
                : 'Article',
            excerpt: excerpt_from_article(a),
            content_html: sanitize_html(a?.content?.rendered),
            link: a.link,
        };
    };

    const fetch_ht_kb_by_id = async id => {
        const res = await fetch(`${KB_WP_REST_BASE}/${id}`);
        if (!res.ok)
            throw new Error(`KB HTTP ${res.status}`);
        const json = await res.json();
        const item = map_kb_article(json);
        if (!item)
            throw new Error('KB article not found');
        return item;
    };

    const fetch_ht_kb_by_slug = async (slug, lang) => {
        const params = new URLSearchParams();
        params.set('per_page', '1');
        params.append('status[]', 'publish');
        params.set('slug', slug);
        if (lang)
            params.set('lang', lang);
        const res = await fetch(`${KB_WP_REST_BASE}?${params.toString()}`);
        if (!res.ok)
            throw new Error(`KB HTTP ${res.status}`);
        const json = await res.json();
        const a = Array.isArray(json) ? json[0] : null;
        return map_kb_article(a);
    };

    const resolve_ht_kb_for_ui = async ({ id, slug, lang }) => {
        const params = new URLSearchParams();
        params.set('lang', lang);
        if (id != null)
            params.set('id', String(id));
        if (slug)
            params.set('slug', slug);
        const res = await fetch(`${KB_WPML_RESOLVE_URL}?${params.toString()}`);
        if (!res.ok)
            throw new Error(`KB resolve HTTP ${res.status}`);
        return res.json();
    };

    const select_kb_article = useCallback((item, share_id = null) => {
        if (!item)
            return;
        if (item.link)
            kb_cache_ref.current.set(item.link, item);
        const sid = share_id != null ? Number(share_id) : kb_wp_id_from_item(item);
        if (sid != null && Number.isFinite(sid))
            kb_share_id_ref.current = sid;
        set_kb_selected(item);
    }, []);

    const open_kb_article = async href => {
        if (!href)
            return;
        const normalized_href = normalize_kb_href(href) || href;
        const cached = kb_cache_ref.current.get(normalized_href);
        if (cached) {
            select_kb_article(cached);
            return;
        }
        const slug = kb_slug_from_url(normalized_href);
        if (!slug)
            return;
        set_kb_detail_loading(true);
        set_kb_detail_error(false);
        try {
            const lng = Utils.i18nUiLang(i18n);
            let item = await fetch_ht_kb_by_slug(slug, lng);
            if (!item) {
                try {
                    const resolved = await resolve_ht_kb_for_ui({ slug, lang: lng });
                    if (resolved?.id)
                        item = await fetch_ht_kb_by_id(resolved.id);
                    if (item && resolved?.source_id != null)
                        select_kb_article(item, resolved.source_id);
                    else if (item)
                        select_kb_article(item);
                    else
                        throw new Error('KB article not found');
                    return;
                } catch (_resolve_err) {
                    // Resolve endpoint may be undeployed: try slug without lang filter.
                    item = await fetch_ht_kb_by_slug(slug, null);
                }
            }
            if (!item?.link)
                throw new Error('KB article not found');
            select_kb_article(item);
        } catch (_err) {
            set_kb_detail_error(true);
            window.open(normalized_href, '_blank', 'noopener,noreferrer');
        } finally {
            set_kb_detail_loading(false);
        }
    };

    // Deep-link from `?kb=<id|slug>`: resolve to UI language, else show source.
    const open_kb_deep_link = async kb_param => {
        const raw = String(kb_param || '').trim();
        if (!raw)
            return;
        set_kb_detail_loading(true);
        set_kb_detail_error(false);
        try {
            const lng = Utils.i18nUiLang(i18n);
            const is_id = /^\d+$/.test(raw);
            let item = null;
            let share_id = is_id ? Number(raw) : null;

            if (!is_id) {
                item = await fetch_ht_kb_by_slug(raw, lng);
                if (item) {
                    select_kb_article(item);
                    return;
                }
            }

            try {
                const resolved = await resolve_ht_kb_for_ui(
                    is_id ? { id: Number(raw), lang: lng } : { slug: raw, lang: lng }
                );
                if (resolved?.source_id != null)
                    share_id = Number(resolved.source_id);
                if (resolved?.id)
                    item = await fetch_ht_kb_by_id(resolved.id);
            } catch (_resolve_err) {
                if (is_id)
                    item = await fetch_ht_kb_by_id(Number(raw));
                else
                    item = await fetch_ht_kb_by_slug(raw, null);
            }

            if (!item?.link)
                throw new Error('KB article not found');
            select_kb_article(item, share_id);
        } catch (_err) {
            set_kb_detail_error(true);
            if (/^\d+$/.test(raw))
                window.open(`https://ticketack.com/?p=${encodeURIComponent(raw)}`, '_blank', 'noopener,noreferrer');
            else
                window.open(
                    `https://ticketack.com/knowledge-base/${encodeURIComponent(raw)}/`,
                    '_blank',
                    'noopener,noreferrer'
                );
        } finally {
            set_kb_detail_loading(false);
        }
    };

    open_kb_article_fn_ref.current = open_kb_article;
    open_kb_deep_link_fn_ref.current = open_kb_deep_link;

    useEffect(() => {
        if (!open)
            return;
        if (!kb_url) {
            set_kb_results([]);
            set_kb_error(false);
            return;
        }
        let cancelled = false;
        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(kb_url, { signal: ac.signal });
                if (!res.ok)
                    throw new Error(`KB HTTP ${res.status}`);
                const json = await res.json();
                if (cancelled)
                    return;
                const mapped = Array.isArray(json)
                    ? json.map(a => map_kb_article(a)).filter(Boolean)
                    : [];
                mapped.forEach(m => kb_cache_ref.current.set(m.link, m));
                set_kb_results(mapped);
                set_kb_error(false);
            } catch (_err) {
                if (cancelled)
                    return;
                set_kb_results([]);
                set_kb_error(true);
            }
        })();
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [kb_url, open]);

    useEffect(() => {
        if (!open)
            kb_auto_opened_for_query_ref.current = null;
    }, [open]);

    useEffect(() => {
        const q = normalize_search(query).trim();
        if (q.length < 3)
            kb_auto_opened_for_query_ref.current = null;
    }, [query]);

    useEffect(() => {
        if (!open || kb_selected != null || kb_error || kb_results.length !== 1 || filtered.length !== 0)
            return;
        // Prefer an explicit `?kb=` deep-link over sole-hit auto-open.
        try {
            if (new URLSearchParams(window.location.search).has(COMMAND_PALETTE_KB_PARAM))
                return;
        } catch (_err) {
            // ignore
        }
        if (pending_kb_ref.current)
            return;
        const q = normalize_search(query).trim();
        if (q.length < 3)
            return;
        if (kb_auto_opened_for_query_ref.current === q)
            return;
        kb_auto_opened_for_query_ref.current = q;
        const sole = kb_results[0];
        const link = sole?.link;
        if (!link)
            return;
        const norm = normalize_kb_href(link);
        if (norm)
            kb_cache_ref.current.delete(norm);
        void open_kb_article_fn_ref.current(link);
    }, [open, kb_selected, kb_error, kb_results, filtered, query]);

    useEffect(() => {
        if (!open)
            return;
        set_kb_selected(null);
        kb_share_id_ref.current = null;
        set_kb_detail_loading(false);
        set_kb_detail_error(false);
    }, [open, query]);

    useEffect(() => {
        set_kb_list_selected(0);
    }, [kb_results, hide_nav_section, open, query]);

    useEffect(() => {
        const on_open = () => {
            track_event('command_palette_open', { source: 'button' });
            set_open(true);
        };
        window.addEventListener('kronos.command_palette.open', on_open);
        return () => window.removeEventListener('kronos.command_palette.open', on_open);
    }, []);

    useEffect(() => {
        if (!open)
            close_kb_lightbox();
    }, [open, close_kb_lightbox]);

    useEffect(() => {
        close_kb_lightbox();
    }, [kb_selected?.id, close_kb_lightbox]);

    useEffect(() => {
        const on_keydown = e => {
            const is_k = (e.key || '').toLowerCase() === 'k';
            const is_open_shortcut = is_k && (e.ctrlKey || e.metaKey);
            if (is_open_shortcut) {
                e.preventDefault();
                track_event('command_palette_open', { source: 'keyboard' });
                set_open(true);
                return;
            }
            if (!open)
                return;
            if (e.key === 'Escape') {
                e.preventDefault();
                if (kb_lightbox_src) {
                    close_kb_lightbox();
                    return;
                }
                set_open(false);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (hide_nav_section && kb_results.length > 0) {
                    set_kb_list_selected(s => Math.min(s + 1, Math.max(0, kb_results.length - 1)));
                } else {
                    set_selected(s => Math.min(s + 1, Math.max(0, filtered.length - 1)));
                }
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (hide_nav_section && kb_results.length > 0) {
                    set_kb_list_selected(s => Math.max(s - 1, 0));
                } else {
                    set_selected(s => Math.max(s - 1, 0));
                }
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (hide_nav_section && kb_results.length > 0) {
                    const pick = kb_results[kb_list_selected] || kb_results[0];
                    if (pick) {
                        track_event('command_palette_select', {
                            kind: 'kb',
                            item_id: String(pick.id || '').slice(0, 100),
                        });
                        select_kb_article(pick);
                    }
                    return;
                }
                const item = filtered[selected];
                if (!item)
                    return;
                track_event('command_palette_select', {
                    kind: 'nav',
                    item_id: String(item.id || '').slice(0, 100),
                    path: String(item.path || '').slice(0, 100),
                });
                set_open(false);
                navigate(item.path);
            }
        };
        window.addEventListener('keydown', on_keydown);
        return () => window.removeEventListener('keydown', on_keydown);
    }, [close_kb_lightbox, filtered, hide_nav_section, kb_lightbox_src, kb_list_selected, kb_results, navigate, open, select_kb_article, selected]);

    useEffect(() => {
        if (!open)
            return;
        // Runs each time the palette is opened. Depends only on `open` so we do
        // not clobber a query when `items` updates while the dialog stays open.
        const params = new URLSearchParams(window.location.search);
        const find_param = params.get(COMMAND_PALETTE_URL_PARAM);
        const mode_param = params.get(COMMAND_PALETTE_MODE_PARAM);
        const kb_param = params.get(COMMAND_PALETTE_KB_PARAM);
        set_context_user_edited(false);
        const path_seed = context_seed_from_pathname(location.pathname, items, t);
        if (find_param !== null) {
            set_query(find_param);
            set_kb_context_lock(mode_param === 'kb' || Boolean(kb_param));
        } else if (kb_param) {
            // Article deep-link alone: do not seed from the current route label.
            set_query('');
            set_kb_context_lock(true);
        } else if (path_seed) {
            set_query(path_seed);
            set_kb_context_lock(true);
        } else {
            set_query('');
            set_kb_context_lock(false);
        }
        set_selected(0);
        set_kb_list_selected(0);
        const select_all = Boolean(
            (find_param != null && (mode_param === 'kb' || Boolean(kb_param)))
            || (find_param == null && !kb_param && path_seed)
        );
        window.setTimeout(() => {
            input_ref.current?.focus?.();
            if (select_all)
                input_ref.current?.select?.();
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // URL → state: open the palette when the route carries ?find=... and/or
    // ?kb=<id|slug> (deep-link from a knowledge-base article, onboarding doc or demo).
    // Also resyncs the query when the URL changes (browser back/forward) while
    // the palette is already open.
    //
    // Important: `open` is intentionally NOT a dependency here. The close
    // cleanup below calls history.replaceState to strip ?find= / ?kb= from the URL,
    // but that does not update react-router's in-memory location — so if we
    // re-ran this effect on `open` changes we would read a stale location.search
    // and re-open the palette right after an Escape / backdrop click.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const deep_link = params.get(COMMAND_PALETTE_URL_PARAM);
        const kb_param = params.get(COMMAND_PALETTE_KB_PARAM);
        if (deep_link === null && (kb_param == null || kb_param === ''))
            return;
        set_open(true);
        if (deep_link !== null)
            set_query(deep_link);
        set_kb_context_lock(
            params.get(COMMAND_PALETTE_MODE_PARAM) === 'kb'
            || Boolean(kb_param)
        );
        if (kb_param)
            pending_kb_ref.current = kb_param;
    }, [location.key, location.search]);

    // Open the article targeted by `?kb=` after mount/clear effects have run.
    useEffect(() => {
        if (!open)
            return;
        const kb_param = pending_kb_ref.current;
        if (!kb_param)
            return;
        const timer = window.setTimeout(() => {
            // Clear after invoking so state→URL does not strip `?kb=` before load starts.
            pending_kb_ref.current = null;
            void open_kb_deep_link_fn_ref.current(kb_param);
        }, 0);
        return () => window.clearTimeout(timer);
    }, [open, location.key, location.search]);

    // State → URL: mirror the live query in ?find=... while the palette is
    // open so the current search can be copied/shared. Use replaceState to
    // avoid polluting the browser history on every keystroke.
    // Param order is always kb → findMode → find.
    useEffect(() => {
        if (!open)
            return;
        try {
            const url = new URL(window.location.href);
            let kb = null;
            if (kb_selected) {
                const share_id = kb_share_id_ref.current != null
                    ? kb_share_id_ref.current
                    : kb_wp_id_from_item(kb_selected);
                if (share_id != null)
                    kb = String(share_id);
            } else if (pending_kb_ref.current || kb_detail_loading) {
                // Keep `kb` while a deep-link is still loading.
                kb = url.searchParams.get(COMMAND_PALETTE_KB_PARAM);
            }
            // When the user leaves the article detail (back to results), drop `kb`.
            apply_command_palette_search_params(url, {
                kb,
                find_mode: (kb_context_lock && !context_user_edited) ? 'kb' : null,
                find: query || null,
            });
            window.history.replaceState(window.history.state, '', url.toString());
        } catch (_err) {
            // ignore
        }
    }, [open, query, kb_context_lock, context_user_edited, kb_selected, kb_detail_loading]);

    // Clean up ?find=... / ?kb=... when the palette closes without navigation, so a
    // plain Esc/backdrop click doesn't leave a stale query in the URL.
    // Skipped on the initial mount (where open is always false) so a user
    // landing on a ?find=... / ?kb=... deep-link actually sees the palette open.
    const was_open_ref = useRef(false);
    useEffect(() => {
        if (open) {
            was_open_ref.current = true;
            return;
        }
        if (!was_open_ref.current)
            return;
        was_open_ref.current = false;
        pending_kb_ref.current = null;
        kb_share_id_ref.current = null;
        try {
            const url = new URL(window.location.href);
            let changed = false;
            if (url.searchParams.has(COMMAND_PALETTE_URL_PARAM)) {
                url.searchParams.delete(COMMAND_PALETTE_URL_PARAM);
                changed = true;
            }
            if (url.searchParams.has(COMMAND_PALETTE_MODE_PARAM)) {
                url.searchParams.delete(COMMAND_PALETTE_MODE_PARAM);
                changed = true;
            }
            if (url.searchParams.has(COMMAND_PALETTE_KB_PARAM)) {
                url.searchParams.delete(COMMAND_PALETTE_KB_PARAM);
                changed = true;
            }
            if (changed)
                window.history.replaceState(window.history.state, '', url.toString());
        } catch (_err) {
            // ignore
        }
    }, [open]);

    useEffect(() => {
        try {
            document.body.classList.toggle('command-palette-open', Boolean(open));
        } catch (_err) {
            // ignore
        }
        return () => {
            try {
                document.body.classList.remove('command-palette-open');
            } catch (_err) {
                // ignore
            }
        };
    }, [open]);

    const on_pick = item => {
        track_event('command_palette_select', {
            kind: 'nav',
            item_id: String(item.id || '').slice(0, 100),
            path: String(item.path || '').slice(0, 100),
        });
        set_open(false);
        navigate(item.path);
    };

    const on_kb_content_click = e => {
        const img_el = e.target?.closest?.('img');
        const picture_el = e.target?.closest?.('picture');
        const el_img = img_el || picture_el?.querySelector?.('img');

        if (el_img) {
            const wrap_a = el_img.closest?.('a');
            if (wrap_a) {
                const raw_a = wrap_a.getAttribute('href');
                const resolved_a = raw_a ? resolve_kb_content_href(raw_a) : null;
                if (resolved_a && is_direct_media_url(resolved_a)) {
                    e.preventDefault();
                    e.stopPropagation();
                    set_kb_lightbox_src(resolved_a.toString());
                    set_kb_lightbox_alt(kb_lightbox_label_for_img(el_img));
                    return;
                }
            } else {
                const src = el_img.currentSrc || el_img.src;
                if (src) {
                    e.preventDefault();
                    e.stopPropagation();
                    set_kb_lightbox_src(src);
                    set_kb_lightbox_alt(kb_lightbox_label_for_img(el_img));
                    return;
                }
            }
        }

        const a = e.target?.closest?.('a');
        if (!a)
            return;
        const raw = a.getAttribute('href');
        if (!raw)
            return;
        const resolved = resolve_kb_content_href(raw);
        if (!resolved)
            return;

        if (is_direct_media_url(resolved)) {
            e.preventDefault();
            e.stopPropagation();
            set_kb_lightbox_src(resolved.toString());
            set_kb_lightbox_alt('');
            return;
        }

        if (is_kb_article_public_url(resolved)) {
            e.preventDefault();
            open_kb_article(resolved.toString());
            return;
        }

        const loc = window.location;
        if (is_kronos_in_app_url(resolved, loc)) {
            e.preventDefault();
            // KB articles often hard-link the production host; still route inside this SPA
            // (current origin) to avoid a full reload. Keep the palette open so context stays visible.
            navigate(`${resolved.pathname}${resolved.search}${resolved.hash}`);
            return;
        }

        e.preventDefault();
        window.open(resolved.toString(), '_blank', 'noopener,noreferrer');
    };

    const kb_lightbox_caption = kb_lightbox_alt.trim();
    const kb_lightbox_portal =
        typeof document !== 'undefined' && kb_lightbox_src
            ? createPortal(
                  <div
                      className="CommandPaletteKbLightbox CommandPaletteKbLightboxRoot"
                      role="dialog"
                      aria-modal="true"
                      aria-label={kb_lightbox_caption || 'Image'}
                      onClick={() => close_kb_lightbox()}
                  >
                      <figure className="CommandPaletteKbLightboxFigure">
                          <img
                              src={kb_lightbox_src}
                              alt={kb_lightbox_caption ? '' : kb_lightbox_alt}
                              className="CommandPaletteKbLightboxImg"
                              draggable={false}
                          />
                          {kb_lightbox_caption ? (
                              <figcaption className="CommandPaletteKbLightboxCaption">
                                  {kb_lightbox_caption}
                              </figcaption>
                          ) : null}
                      </figure>
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <Modal
                isOpen={open}
                toggle={() => set_open(false)}
                centered
                backdrop
                className="CommandPaletteModal"
                modalClassName="CommandPaletteModalDialog"
                contentClassName="CommandPaletteModalContent"
            >
            <ModalBody className="CommandPaletteBody">
                <Input
                    innerRef={input_ref}
                    type="search"
                    name="kronos-command-palette-search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={query}
                    onChange={e => {
                        set_context_user_edited(true);
                        set_query(e.target.value);
                    }}
                    placeholder={t('common.search') || t('common.search_placeholder') || 'Search…'}
                    className="CommandPaletteInput"
                />
                <div
                    className={`CommandPaletteResults ${kb_selected ? 'CommandPaletteResults--detail' : ''}`}
                    role="listbox"
                    aria-label="Command palette results"
                >
                    {!kb_selected && nav_shown_in_ui && (
                        <div className="CommandPaletteSection CommandPaletteSection--first">
                            <div className="CommandPaletteSectionTitle">
                                <span className="CommandPaletteSectionTitleLeft">
                                    <span className="CommandPaletteKIcon" aria-hidden="true">K</span>
                                    {t('common.command_palette_nav_title') || 'Navigation Kronos'}
                                </span>
                            </div>
                            <div className="CommandPaletteNavResults">
                                {filtered.slice(0, 30).map((item, idx) => {
                                    const crumbs = [item.section_label, item.group_label].filter(Boolean);
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={`CommandPaletteItem ${idx === selected ? 'isSelected' : ''}`}
                                            onMouseEnter={() => set_selected(idx)}
                                            onClick={() => on_pick(item)}
                                            role="option"
                                            aria-selected={idx === selected}
                                        >
                                            <span className="CommandPaletteItemTopRow">
                                                <span className="CommandPaletteItemLabel">
                                                    {crumbs.map((c, i) => (
                                                        <React.Fragment key={i}>
                                                            <span className="CommandPaletteItemCrumb">{c}</span>
                                                            <span className="CommandPaletteItemCrumbSep" aria-hidden="true">{' \u203A '}</span>
                                                        </React.Fragment>
                                                    ))}
                                                    <span className="CommandPaletteItemCrumbCurrent">{item.label}</span>
                                                </span>
                                                <span className="CommandPaletteItemPath">{item.path}</span>
                                            </span>
                                            {item.description && <span className="CommandPaletteItemDesc">{item.description}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {(kb_selected || kb_results.length > 0 || kb_error) && (
                        <div className={`CommandPaletteSection ${kb_selected ? 'CommandPaletteSection--first' : ''}`}>
                            {!kb_selected && (
                                <div className="CommandPaletteSectionTitle">
                                    <span className="CommandPaletteSectionTitleLeft">
                                        <i className="fa fa-question-circle CommandPaletteSectionIcon" aria-hidden="true"></i>
                                        {t('common.knowledge_base') || 'Knowledge base'}
                                    </span>
                                </div>
                            )}
                            {kb_selected ? (
                                <div className="CommandPaletteKbDetail">
                                    <div className="CommandPaletteKbDetailTop">
                                        <button
                                            type="button"
                                            className="CommandPaletteKbBackRow"
                                            onClick={() => {
                                                kb_share_id_ref.current = null;
                                                set_kb_selected(null);
                                            }}
                                            aria-label={
                                                kb_results.length > 0
                                                    ? `${t('common.command_palette_kb_back_to_results')} (${kb_results.length})`
                                                    : (t('common.back') || 'Retour')
                                            }
                                        >
                                            <i className="fa fa-arrow-left" aria-hidden="true"></i>
                                            {kb_results.length > 0 ? (
                                                <>
                                                    <span className="CommandPaletteKbBackRowLabel">
                                                        {t('common.command_palette_kb_back_to_results')}
                                                    </span>
                                                    <span className="CommandPaletteKbBackRowBadge" aria-hidden="true">
                                                        {kb_results.length}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="CommandPaletteKbBackRowLabel">
                                                    {t('common.back') || 'Retour'}
                                                </span>
                                            )}
                                        </button>
                                        <div className="CommandPaletteKbDetailContext">
                                            <span className="CommandPaletteKbDetailContextKb">
                                                <i className="fa fa-question-circle CommandPaletteSectionIcon" aria-hidden="true"></i>
                                                {t('common.knowledge_base') || 'Knowledge base'}
                                            </span>
                                            <span className="CommandPaletteKbDetailContextSep" aria-hidden="true">
                                                {'\u203a'}
                                            </span>
                                            <span className="CommandPaletteKbDetailContextArticle">
                                                <span className="CommandPaletteKbDetailArticleTitle">{kb_selected.title}</span>
                                                <a
                                                    className="CommandPaletteKbOpen CommandPaletteKbOpen--adjacent"
                                                    href={kb_selected.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    aria-label={t('common.open_in_kb') || 'Ouvrir dans la base de connaissances'}
                                                    title={t('common.open_in_kb') || 'Ouvrir dans la base de connaissances'}
                                                >
                                                    <i className="fa fa-link" aria-hidden="true"></i>
                                                </a>
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className="CommandPaletteKbContent"
                                        onClick={on_kb_content_click}
                                        dangerouslySetInnerHTML={{ __html: kb_selected.content_html || '' }}
                                    />
                                    {kb_detail_loading && (
                                        <div className="CommandPaletteKbMeta">
                                            {t('common.loading') || 'Chargement…'}
                                        </div>
                                    )}
                                    {kb_detail_error && (
                                        <div className="CommandPaletteKbMeta">
                                            {t('common.error') || 'Erreur'}
                                        </div>
                                    )}
                                </div>
                            ) : kb_results.length > 0 ? (
                                <div className="CommandPaletteKbResults">
                                    {kb_results.map((r, kidx) => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            className={`CommandPaletteKbItem ${hide_nav_section && kidx === kb_list_selected ? 'isSelected' : ''}`}
                                            onMouseEnter={() => set_kb_list_selected(kidx)}
                                            onClick={() => {
                                                track_event('command_palette_select', {
                                                    kind: 'kb',
                                                    item_id: String(r.id || '').slice(0, 100),
                                                });
                                                select_kb_article(r);
                                            }}
                                        >
                                            <div className="CommandPaletteKbTitle">{r.title}</div>
                                            {r.excerpt && <div className="CommandPaletteKbExcerpt">{r.excerpt}</div>}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="CommandPaletteKbError">
                                    {t('common.kb_unavailable') || 'Knowledge base results unavailable.'}
                                </div>
                            )}
                        </div>
                    )}

                    {!kb_selected && show_contact_panel && !kb_error && (
                        <div className={`CommandPaletteEmpty ${nav_shown_in_ui ? '' : 'CommandPaletteEmpty--first'}`}>
                            <div className="CommandPaletteEmptyTitle">
                                {t('common.command_palette_contact_title') || 'Vous ne trouvez pas ce que vous cherchez ?'}
                            </div>
                            <div className="CommandPaletteEmptyText">
                                {t('common.command_palette_contact_intro') || 'Si vous rencontrez des difficultés ou avez des questions, vous pouvez nous contacter de différentes manières :'}
                            </div>
                            <ul className="CommandPaletteEmptyList">
                                <li>{t('common.command_palette_contact_signal') || 'Sur le groupe Signal dédié à votre organisation (le cas échéant)'}</li>
                                <li>{t('common.command_palette_contact_phone') || 'Par téléphone au 022 364 8000'}</li>
                                <li>{t('common.command_palette_contact_emergency') || 'En cas d’urgence, hors des heures d’ouverture, en appelant le 022 777 74 91 et en suivant les instructions du répondeur 24/7'}</li>
                                <li>{t('common.command_palette_contact_email') || 'Par e-mail à l’adresse support@netoxygen.ch'}</li>
                            </ul>
                        </div>
                    )}
                </div>
                <div className="CommandPaletteHints">
                    <div className="CommandPaletteHint">
                        <span className="CommandPaletteHintLabel">{t('common.open_search_bar')}</span>
                        <span className="CommandPaletteHintKeys">
                            <span className="CommandPaletteKey">Ctrl</span>
                            <span className="CommandPaletteKey">K</span>
                        </span>
                    </div>
                    <div className="CommandPaletteHint">
                        <span className="CommandPaletteHintLabel">{t('common.close_search_bar')}</span>
                        <span className="CommandPaletteHintKeys">
                            <span className="CommandPaletteKey">esc</span>
                        </span>
                    </div>
                </div>
            </ModalBody>
            </Modal>
            {kb_lightbox_portal}
        </>
    );
}

